import classNames from 'classnames';
import { memo } from 'react';
import isEqual from 'react-fast-compare';

import { PauseIcon, SkipForwardIcon, XIcon } from '@air/design-icons';
import {
  FileStatusButton,
  FileStatusPanelItem,
  FileStatusSubtitle,
  FileStatusThumbnail,
  FileStatusTitle,
  type FileTrackingItem,
} from '@air/feature-file-status/ui';
import { getSubtitleWithProgress } from '@air/feature-file-status/utils';
import { UploadStatus } from '@air/redux-uploader';

import { SHARED_PANEL_ITEM_CLASS_NAMES } from '../../constants';

export type FileStatusTrackingPanelListItemProgressingProps = {
  item: FileTrackingItem;
  onCancel?: (item: FileTrackingItem) => void;
  onPause?: (item: FileTrackingItem) => void;
  onSkip?: (item: FileTrackingItem) => void;
};

export const FileStatusTrackingPanelListItemProgressing = memo(
  ({
    item,
    item: {
      title,
      ext,
      status,
      onRowClick,
      previewLoader,
      thumbnailSource,
      avatar,
      subtitle,
      sizeInBytes,
      currentBytesUploaded,
    },
    onSkip,
    onCancel,
    onPause,
  }: FileStatusTrackingPanelListItemProgressingProps) => (
    <FileStatusPanelItem
      avatar={avatar ?? <FileStatusThumbnail ext={ext} status={status} thumbnailSource={thumbnailSource} />}
      buttons={
        <>
          {onPause && (
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            <FileStatusButton appearance="ghost" label="Pause" onClick={() => onPause(item)}>
              <PauseIcon size="sm" />
            </FileStatusButton>
          )}
          {onSkip && (
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            <FileStatusButton appearance="ghost" label="Skip" onClick={() => onSkip(item)}>
              <SkipForwardIcon size="sm" />
            </FileStatusButton>
          )}
          {onCancel && (
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            <FileStatusButton appearance="ghost" label="Cancel" onClick={() => onCancel(item)}>
              <XIcon size="sm" />
            </FileStatusButton>
          )}
        </>
      }
      className={classNames(
        'hover:ads:bg-hover-subtle hover:bg-gray-3',
        SHARED_PANEL_ITEM_CLASS_NAMES,
        !!onRowClick && 'cursor-pointer',
      )}
      onClick={onRowClick}
      previewLoader={previewLoader}
      subtitle={
        status === UploadStatus.uploading ? (
          <FileStatusSubtitle className="truncate">
            {subtitle ??
              getSubtitleWithProgress({
                sizeInBytes,
                title: 'Uploading...',
                uploadedBytes: currentBytesUploaded,
              })}
          </FileStatusSubtitle>
        ) : (
          <FileStatusSubtitle>Queued</FileStatusSubtitle>
        )
      }
      title={<FileStatusTitle className="text-12">{title}</FileStatusTitle>}
    />
  ),
  isEqual,
);

FileStatusTrackingPanelListItemProgressing.displayName = 'FileStatusTrackingPanelListItemProgressing';
